import { useEffect, useState } from "react";
import { Form, router, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card";
import { Choice } from "../../requests/controls";
import { formatLocationTime } from "@/lib/location-time";

export type WhatsappSetupProps = {
  available: boolean;
  environment: string;
  locations: { id: string; name: string }[];
  launch: {
    state: string;
    app_id: string;
    config_id: string;
    version: string;
  } | null;
  authorization: {
    id: string;
    location_name: string;
    numbers: { id: string; name: string }[];
  } | null;
  numbers: {
    id: string;
    name: string;
    location_id: string;
    ready: boolean;
    state: string;
    verified_at: string | null;
  }[];
};

export function WhatsappSetup({ setup }: { setup: WhatsappSetupProps }) {
  const { t } = useTranslation();
  const { i18n } = usePage().props;
  const [sdkReady, setSdkReady] = useState(false);
  const [failure, setFailure] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!setup.launch || !setup.available) return;
    let active = true;
    const initialize = () => {
      if (window.FB && active) {
        window.FB.init({
          appId: setup.launch!.app_id,
          version: setup.launch!.version,
          xfbml: false,
          autoLogAppEvents: false,
        });
        setSdkReady(true);
      }
    };
    const existing = document.getElementById("whatsapp-facebook-sdk");
    const script = existing || document.createElement("script");
    script.addEventListener("load", initialize);
    const failed = () => {
      if (active) setFailure(true);
    };
    script.addEventListener("error", failed);
    if (!existing) {
      script.id = "whatsapp-facebook-sdk";
      (script as HTMLScriptElement).src =
        "https://connect.facebook.net/en_US/sdk.js";
      (script as HTMLScriptElement).async = true;
      document.head.appendChild(script);
    }
    initialize();
    return () => {
      active = false;
      script.removeEventListener("load", initialize);
      script.removeEventListener("error", failed);
    };
  }, [setup.available, setup.launch]);
  const signup = () => {
    if (!window.FB || !setup.launch || busy) return;
    setBusy(true);
    setFailure(false);
    window.FB.login(
      (response) => {
        const code = response.authResponse?.code;
        if (typeof code !== "string" || code.length === 0) {
          setBusy(false);
          setFailure(true);
          return;
        }
        router.patch(
          "/user/whatsapp_authorization",
          { whatsapp_authorization: { state: setup.launch!.state, code } },
          { onFinish: () => setBusy(false) },
        );
      },
      {
        config_id: setup.launch.config_id,
        response_type: "code",
        override_default_response_type: true,
        extras: { setup: {} },
      },
    );
  };
  return (
    <Card>
      <CardHeader>
        <CardDescription>{t("whatsapp.description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Badge variant="outline">
          {t(
            `whatsapp.environment.${setup.environment === "live" ? "live" : "test"}`,
          )}
        </Badge>
        <p className="text-muted-foreground text-sm">
          {t("whatsapp.constraints")}
        </p>
        <a
          className="inline-block text-sm underline underline-offset-4"
          href="https://developers.facebook.com/documentation/business-messaging/whatsapp/embedded-signup/implementation"
          target="_blank"
          rel="noreferrer"
        >
          {t("whatsapp.guide")}
        </a>
        {!setup.available && (
          <p className="text-muted-foreground text-sm">
            {t("whatsapp.unavailable")}
          </p>
        )}
        {failure && (
          <p role="alert" className="text-sm text-destructive">
            {t("whatsapp.unavailable")}
          </p>
        )}
        {setup.launch && setup.available && (
          <Button type="button" disabled={!sdkReady || busy} onClick={signup}>
            {t("whatsapp.continue")}
          </Button>
        )}
        {setup.available && setup.locations.length > 0 && (
          <Form action="/user/whatsapp_authorization" method="post">
            {({ processing }) => (
              <div className="space-y-3">
                <Choice
                  id="whatsapp-intake-location"
                  name="whatsapp_authorization[location_id]"
                  label={t("whatsapp.default_location")}
                  options={setup.locations}
                  defaultValue={setup.locations[0].id}
                />
                <Button type="submit" disabled={processing}>
                  {t("whatsapp.authorize")}
                </Button>
              </div>
            )}
          </Form>
        )}
        {setup.available && setup.authorization && (
          <section
            className="space-y-3 rounded-md border p-3"
            aria-label={t("whatsapp.choose_number")}
          >
            <h3 className="font-medium">{t("whatsapp.choose_number")}</h3>
            <p className="text-muted-foreground text-sm">
              {t("whatsapp.intake_at", {
                location: setup.authorization.location_name,
              })}
            </p>
            {setup.authorization.numbers.length === 0 ? (
              <p>{t("whatsapp.no_numbers")}</p>
            ) : (
              <Form action="/user/whatsapp_numbers" method="post">
                {({ processing }) => (
                  <div className="space-y-3">
                    <input
                      type="hidden"
                      name="whatsapp_number[authorization_id]"
                      value={setup.authorization!.id}
                    />
                    <Choice
                      id="whatsapp-number"
                      name="whatsapp_number[phone_id]"
                      label={t("whatsapp.number")}
                      options={setup.authorization!.numbers}
                      defaultValue={setup.authorization!.numbers[0].id}
                    />
                    <div className="flex items-start gap-2">
                      <Checkbox
                        id="whatsapp-company-owned"
                        name="whatsapp_number[company_owned]"
                        value="1"
                        required
                      />
                      <Label htmlFor="whatsapp-company-owned">
                        {t("whatsapp.company_owned")}
                      </Label>
                    </div>
                    <Button type="submit" disabled={processing}>
                      {t("whatsapp.connect")}
                    </Button>
                  </div>
                )}
              </Form>
            )}
          </section>
        )}
        {setup.numbers.map((profile) => (
          <section
            className="space-y-3 rounded-md border p-3"
            key={profile.id}
            aria-label={profile.name}
          >
            <h3 className="font-medium break-words">{profile.name}</h3>
            <p className="text-sm">
              {t(
                `whatsapp.states.${profile.ready ? "ready" : profile.state === "pending" ? "pending" : profile.state === "disconnected" ? "disconnected" : "unavailable"}`,
              )}
            </p>
            {profile.verified_at && (
              <p className="text-muted-foreground text-sm">
                {t("whatsapp.checked", {
                  date: formatLocationTime(profile.verified_at, i18n, {
                    dateStyle: "medium",
                    timeStyle: "short",
                  }),
                })}
              </p>
            )}
            <Form
              action={`/user/whatsapp_numbers/${profile.id}`}
              method="patch"
            >
              {({ processing }) => (
                <div className="space-y-3">
                  <Choice
                    id={`whatsapp-location-${profile.id}`}
                    name="whatsapp_number[location_id]"
                    label={t("whatsapp.default_location")}
                    options={setup.locations}
                    defaultValue={profile.location_id}
                  />
                  <Button type="submit" variant="outline" disabled={processing}>
                    {t("whatsapp.save_location")}
                  </Button>
                </div>
              )}
            </Form>
            {profile.state !== "disconnected" && (
              <Form
                action={`/user/whatsapp_numbers/${profile.id}`}
                method="delete"
              >
                {({ processing }) => (
                  <Button type="submit" variant="outline" disabled={processing}>
                    {t("whatsapp.disconnect")}
                  </Button>
                )}
              </Form>
            )}
          </section>
        ))}
      </CardContent>
    </Card>
  );
}

// Only the supported SDK callback is consumed; browser postMessage asset claims
// are never used. The backend independently discovers all WABAs and numbers.
declare global {
  interface Window {
    FB?: {
      init(options: {
        appId: string;
        version: string;
        xfbml: boolean;
        autoLogAppEvents: boolean;
      }): void;
      login(
        callback: (response: { authResponse?: { code?: string } }) => void,
        options: {
          config_id: string;
          response_type: string;
          override_default_response_type: boolean;
          extras: { setup: Record<string, never> };
        },
      ): void;
    };
  }
}
